import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { storage as storageEnums } from '../utils/catalog';
import { StorageService } from './storage.service';

export interface RequestHeaders {
  [header: string]: string | string[];
}

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private baseUrl: string;

  constructor(private http: HttpClient, private storage: StorageService) {
    this.baseUrl = environment.api.url;
  }

  private getRequestUrl(url: string) {
    if (!url) return this.baseUrl;
    if (url.startsWith('http')) return url;
    return `${this.baseUrl}/${url}`;
  }

  private getRequestHeaders(auth: any, otherHeaders: RequestHeaders = {}) {
    const headers: any = { 'content-type': 'application/json' };
    if (auth?.sessionId) headers['x-session-id'] = auth?.sessionId;

    return _.merge(headers, otherHeaders);
  }

  public async getHeaders(otherHeaders: RequestHeaders = {}): Promise<RequestHeaders> {
    const auth = await this.storage.get(storageEnums.keys.auth, {});

    return this.getRequestHeaders(auth, otherHeaders);
  }

  public setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  public getBaseUrl(): string {
    return this.baseUrl;
  }

  public async get(url: string, query: any = {}, response: boolean = false): Promise<any> {
    const options: any = {
      headers: await this.getHeaders(),
      params: new HttpParams({
        fromObject: _.omitBy(query, (value) => value == null),
      }),
      observe: response ? 'response' : undefined,
      withCredentials: true,
    };

    return lastValueFrom(this.http.get(this.getRequestUrl(url), options));
  }

  public async post(url: string, data: any = undefined): Promise<any> {
    return lastValueFrom(
      this.http.post(this.getRequestUrl(url), JSON.stringify(data), {
        headers: await this.getHeaders(),
        withCredentials: true,
      })
    );
  }

  public async put(url: string, data: any = undefined): Promise<any> {
    return lastValueFrom(
      this.http.put(this.getRequestUrl(url), JSON.stringify(data), {
        headers: await this.getHeaders(),
        withCredentials: true,
      })
    );
  }

  public async patch(url: string, data: any = undefined): Promise<any> {
    return lastValueFrom(
      this.http.patch(this.getRequestUrl(url), JSON.stringify(data), {
        headers: await this.getHeaders(),
        withCredentials: true,
      })
    );
  }

  public async delete(url: string): Promise<any> {
    return lastValueFrom(
      this.http.delete(this.getRequestUrl(url), {
        headers: await this.getHeaders(),
        withCredentials: true,
      })
    );
  }

  public async downloadFile(url: string, query: any = {}): Promise<any> {
    return lastValueFrom(
      this.http.get(this.getRequestUrl(url), {
        headers: await this.getHeaders(),
        params: new HttpParams({
          fromObject: _.omitBy(query, (value) => value == null),
        }),
        responseType: 'blob' as 'json',
        withCredentials: true,
      })
    );
  }
}
